import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import AuthLayout, { FormError, PasswordInput, TextLink } from '../components/AuthLayout';

const MIN_PASSWORD = 10;

function errorMessage(err, fallback) {
  const detail = err.response?.data?.detail;
  if (Array.isArray(detail)) {
    return detail.map((d) => d?.msg || '').filter(Boolean).join('. ') || fallback;
  }
  return typeof detail === 'string' ? detail : fallback;
}

export default function RegisterPage() {
  const [form, setForm] = useState({ email: '', password: '', full_name: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { register } = useAuth();
  const navigate = useNavigate();

  const longEnough = form.password.length >= MIN_PASSWORD;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!longEnough) {
      setError(`Use at least ${MIN_PASSWORD} characters for your password.`);
      return;
    }
    setLoading(true);
    try {
      await register(form.email, form.password, form.full_name);
      toast.success('Account created');
      navigate('/dashboard');
    } catch (err) {
      setError(errorMessage(err, "We couldn't create your account. Try again in a moment."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout
      title="Create your account"
      subtitle="Free during the pilot. No card needed to start."
      footer={<>Already have an account? <TextLink to="/login" data-testid="go-to-login">Sign in</TextLink></>}
    >
      <form onSubmit={handleSubmit} className="flex flex-col gap-5">
        <FormError>{error}</FormError>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="register-name">Full name</Label>
          <Input
            id="register-name"
            data-testid="register-name-input"
            autoComplete="name"
            value={form.full_name}
            onChange={(e) => setForm({ ...form, full_name: e.target.value })}
            placeholder="Jordan Landry"
            className="h-12"
            required
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="register-email">Email</Label>
          <Input
            id="register-email"
            data-testid="register-email-input"
            type="email"
            autoComplete="email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            placeholder="you@company.com"
            className="h-12"
            required
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="register-password">Password</Label>
          <PasswordInput
            id="register-password"
            data-testid="register-password-input"
            toggleTestId="toggle-register-password"
            autoComplete="new-password"
            aria-describedby="register-password-rule"
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            required
          />
          <p
            id="register-password-rule"
            className={`m-0 flex items-center gap-1.5 text-sm leading-5 ${longEnough ? 'font-bold text-sev-low' : 'text-ink-muted'}`}
          >
            {longEnough && <Check className="h-4 w-4" strokeWidth={2} aria-hidden="true" />}
            At least {MIN_PASSWORD} characters
          </p>
        </div>

        <Button data-testid="register-submit-button" type="submit" size="lg" disabled={loading} className="mt-1 w-full">
          {loading ? 'Creating account…' : 'Create account'}
        </Button>
      </form>
    </AuthLayout>
  );
}
